import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import styles from './MenuPage.module.css';
import HeroSection from '../../sections/HeroSection/HeroSection';

// ─────────────────────────────────────────────────────────────────────────────
// GOOGLE SHEETS CONFIG
// Keep these URLs pointing to the published Google Sheet tabs.
// ─────────────────────────────────────────────────────────────────────────────

const SHEET_URLS = {
  'Food Menu':
    'https://docs.google.com/spreadsheets/d/e/2PACX-1vQzdh1j4UJZQPdIPj9NFNSN5C1los0kzn-HSIU9YnUHHl3Rpo-6-klB2NbitAhpH7Zp14-2RquvnoqZ/pub?gid=661332341&single=true&output=csv',

  'Bar Menu':
    'https://docs.google.com/spreadsheets/d/e/2PACX-1vQzdh1j4UJZQPdIPj9NFNSN5C1los0kzn-HSIU9YnUHHl3Rpo-6-klB2NbitAhpH7Zp14-2RquvnoqZ/pub?gid=463877576&single=true&output=csv',

  Cocktails:
    'https://docs.google.com/spreadsheets/d/e/2PACX-1vQzdh1j4UJZQPdIPj9NFNSN5C1los0kzn-HSIU9YnUHHl3Rpo-6-klB2NbitAhpH7Zp14-2RquvnoqZ/pub?gid=1900464692&single=true&output=csv',

  Mocktails:
    'https://docs.google.com/spreadsheets/d/e/2PACX-1vQzdh1j4UJZQPdIPj9NFNSN5C1los0kzn-HSIU9YnUHHl3Rpo-6-klB2NbitAhpH7Zp14-2RquvnoqZ/pub?gid=500624855&single=true&output=csv',

  'On Tap':
    'https://docs.google.com/spreadsheets/d/e/2PACX-1vQzdh1j4UJZQPdIPj9NFNSN5C1los0kzn-HSIU9YnUHHl3Rpo-6-klB2NbitAhpH7Zp14-2RquvnoqZ/pub?gid=1552719221&single=true&output=csv',
};





// Exact main tabs
const MAIN_CATEGORIES = [
  'Food Menu',
  'Bar Menu',
  'Cocktails',
  'Mocktails',
  'On Tap',
];

// Exact order from the updated spreadsheet / PDF
const FOOD_SUBCATEGORIES = [
  'Soups',
  'Salads',
  'Bar Appetizers',
  'Indian Veg Appetizers',
  'Indian Non-Veg Appetizers',
  'Oriental Appetizers',
  'Pizzas',
  'Pasta & Risotto',
  'Indian Veg Mains',
  'Indian Non-Veg Mains',
  'Oriental/Continental Mains',
  'Indian Breads',
  'Biryani & Rice',
  'Desserts',
];

const BAR_SUBCATEGORIES = [
  'Liqueur & Aperitivo',
  'Indian Single Malt',
  'Single Malt',
  'Blended Scotch Whisky',
  'Irish Whiskey',
  'Bourbon & Tennessee Whiskey',
  'Japanese Whisky',
  'Gin',
  'Vodka',
  'Rum',
  'Tequila & Mezcal',
  'Brandy & Cognac',
  'Wines',
  'Rosé & Sparkling Wine',
  'Other Beverages',
  'Shakes',
  'Hot Beverages',
];

const COCKTAIL_SUBCATEGORIES = [
  'Signature Cocktails',
  'Classic Cocktails',
  'Beer Tails',
  'Shots',
];

const MOCKTAIL_SUBCATEGORIES = ['Mocktails'];

const ON_TAP_SUBCATEGORIES = ['Berli Craft Beers'];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const cleanValue = (value) => {
  if (value === null || value === undefined) return '';

  return String(value)
    .replace(/\uFEFF/g, '')
    .replace(/\r/g, '')
    .trim();
};

const isTruthy = (value) => {
  return String(value || '').trim().toUpperCase() === 'TRUE';
};

const parseNumber = (value, fallback = 999999) => {
  const number = Number(String(value || '').replace(/,/g, '').trim());
  return Number.isFinite(number) ? number : fallback;
};

const hasValue = (value) => {
  return cleanValue(value) !== '' && cleanValue(value) !== '-';
};

// ─────────────────────────────────────────────────────────────────────────────
// ROBUST CSV PARSER
// Handles commas inside quoted descriptions + escaped quotes.
// ─────────────────────────────────────────────────────────────────────────────

const parseCSVLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      // Escaped quote: ""
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());

  return values.map(cleanValue);
};

const parseCSV = (text) => {
  if (!text || !text.trim()) return [];

  const normalizedText = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/^\uFEFF/, '')
    .trim();

  const lines = normalizedText.split('\n');

  if (lines.length < 2) return [];

  const headers = parseCSVLine(lines[0]);

  return lines
    .slice(1)
    .map((line) => parseCSVLine(line))
    .map((values) => {
      const row = {};

      headers.forEach((header, index) => {
        row[header] = cleanValue(values[index] || '');
      });

      return row;
    })
    .filter((row) => hasValue(row.name || row.Name));
};

// ─────────────────────────────────────────────────────────────────────────────
// NORMALIZE GOOGLE SHEET ROWS
// This makes the frontend tolerant if Google Sheets changes capitalization.
// ─────────────────────────────────────────────────────────────────────────────

const normalizeRow = (raw) => {
  const get = (...keys) => {
    for (const key of keys) {
      if (hasValue(raw?.[key])) {
        return cleanValue(raw[key]);
      }
    }

    return '';
  };

  return {
    name: get('name', 'Name'),
    description: get('description', 'Description'),
    subcategory: get('subcategory', 'Subcategory'),
    price: get('price', 'Price'),

    price_30ml: get(
      'price_30ml',
      'price 30ml',
      'Price_30ml',
      'Price 30ml',
      '30ml'
    ),

    price_60ml: get(
      'price_60ml',
      'price 60ml',
      'Price_60ml',
      'Price 60ml',
      '60ml'
    ),

    price_bottle: get(
      'price_bottle',
      'price bottle',
      'Price_bottle',
      'Price bottle',
      'Bottle'
    ),

    price_glass: get(
      'price_glass',
      'price glass',
      'Price_glass',
      'Price glass',
      'Glass'
    ),

    price_250ml: get(
      'price_250ml',
      'price 250ml',
      'Price_250ml',
      'Price 250ml',
      '250ml'
    ),

    price_500ml: get(
      'price_500ml',
      'price 500ml',
      'Price_500ml',
      'Price 500ml',
      '500ml'
    ),

    serving_size: get(
      'serving_size',
      'serving size',
      'Serving_Size',
      'Serving Size'
    ),

    variants: get('variants', 'Variants'),
    variant_prices: get('variant_prices', 'Variant Prices'),
    dietary: get('dietary', 'Dietary'),

    sort_order: get('sort_order', 'Sort Order'),
    source_note: get('source_note', 'Source Note'),

    isVeg: get('isVeg', 'isveg', 'IsVeg'),
    isNew: get('isNew', 'isnew', 'IsNew'),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// FETCH SHEET
// ─────────────────────────────────────────────────────────────────────────────

const fetchSheet = async (url) => {
  // Cache-busting is useful because Google published CSV can sometimes be cached.
  const requestUrl = new URL(url);
  requestUrl.searchParams.set('_cache', Date.now().toString());

  const response = await fetch(requestUrl.toString(), {
    method: 'GET',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Unable to load menu. HTTP ${response.status}`);
  }

  const text = await response.text();

  if (
    text.includes('<!DOCTYPE html') ||
    text.includes('<html') ||
    text.trim().startsWith('<!')
  ) {
    throw new Error(
      'Google Sheet is not published correctly. Use File → Share → Publish to web → CSV.'
    );
  }

  return parseCSV(text).map(normalizeRow);
};

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ALL MENU DATA
// ─────────────────────────────────────────────────────────────────────────────

const useAllMenuData = () => {
  const [allData, setAllData] = useState({});
  const [statuses, setStatuses] = useState(
    Object.fromEntries(
      MAIN_CATEGORIES.map((category) => [category, 'loading'])
    )
  );
  const [errors, setErrors] = useState({});

  const fetchAll = useCallback(async () => {
    setStatuses(
      Object.fromEntries(
        MAIN_CATEGORIES.map((category) => [category, 'loading'])
      )
    );

    setErrors({});

    try {
      const results = await Promise.allSettled(
        MAIN_CATEGORIES.map(async (category) => {
          const rows = await fetchSheet(SHEET_URLS[category]);

          return {
            category,
            rows,
          };
        })
      );

      const nextData = {};
      const nextStatuses = {};
      const nextErrors = {};

      results.forEach((result, index) => {
        const category = MAIN_CATEGORIES[index];

        if (result.status === 'fulfilled') {
          nextData[category] = result.value.rows;
          nextStatuses[category] = 'done';
        } else {
          nextData[category] = [];
          nextStatuses[category] = 'error';

          nextErrors[category] =
            result.reason?.message || 'Failed to load menu data.';
        }
      });

      setAllData(nextData);
      setStatuses(nextStatuses);
      setErrors(nextErrors);
    } catch (error) {
      console.error('Menu loading error:', error);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    allData,
    statuses,
    errors,
    refetch: fetchAll,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// BADGES
// ─────────────────────────────────────────────────────────────────────────────

const VegBadge = ({ isVeg }) => {
  if (isTruthy(isVeg)) {
    return (
      <span
        className={styles.vegBadge}
        title="Vegetarian"
        aria-label="Vegetarian"
      >
        <span className={styles.vegDot} />
      </span>
    );
  }

  return (
    <span
      className={styles.nonVegBadge}
      title="Non-Vegetarian"
      aria-label="Non-Vegetarian"
    >
      <span className={styles.nonVegDot} />
    </span>
  );
};

const NewBadge = () => (
  <span className={styles.newBadge} aria-label="New">
    New
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// FOOD VARIANT PRICING
//
// Example:
// Veg Manchow Soup / Chicken
// 399 / 429
//
// Keeps the original item name while presenting its variants more clearly.
// ─────────────────────────────────────────────────────────────────────────────

const VariantPrice = ({ item }) => {
  const price = cleanValue(item.price);

  if (!price) return null;

  // Example: 399 / 429
  if (!price.includes('/')) {
    return <p className={styles.itemPrice}>{price}</p>;
  }

  const priceParts = price
    .split('/')
    .map((part) => cleanValue(part))
    .filter(Boolean);

  if (priceParts.length <= 1) {
    return <p className={styles.itemPrice}>{price}</p>;
  }

  const nameParts = cleanValue(item.name)
    .split('/')
    .map((part) => cleanValue(part))
    .filter(Boolean);

  const canMapNames = nameParts.length === priceParts.length;

  if (!canMapNames) {
    return <p className={styles.itemPrice}>{price}</p>;
  }

  return (
    <div className={styles.barPriceBlock}>
      {priceParts.map((priceValue, index) => (
        <span className={styles.barPriceUnit} key={`${priceValue}-${index}`}>
          <span className={styles.barPriceLabel}>
            {nameParts[index].replace(/^[-\s]+|[-\s]+$/g, '')}
          </span>
          {priceValue}
        </span>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BAR / SPIRIT PRICE DISPLAY
// ─────────────────────────────────────────────────────────────────────────────

const SpiritPrice = ({ item }) => {
  const has30 = hasValue(item.price_30ml);
  const has60 = hasValue(item.price_60ml);
  const hasBottle = hasValue(item.price_bottle);

  if (!has30 && !has60 && !hasBottle) {
    return null;
  }

  return (
    <div className={styles.barPriceBlock}>
      {has30 && (
        <span className={styles.barPriceUnit}>
          <span className={styles.barPriceLabel}>30ml</span>
          {item.price_30ml}
        </span>
      )}

      {has60 && (
        <span className={styles.barPriceUnit}>
          <span className={styles.barPriceLabel}>60ml</span>
          {item.price_60ml}
        </span>
      )}

      {hasBottle && (
        <span className={styles.barPriceUnit}>
          <span className={styles.barPriceLabel}>Bottle</span>
          {item.price_bottle}
        </span>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WINE PRICE DISPLAY
//
// Example:
// 375ml / 750ml
// 1549 / 2899
// ─────────────────────────────────────────────────────────────────────────────

const WinePrice = ({ item }) => {
  const price = cleanValue(item.price);

  if (!price) return null;

  const serving = cleanValue(item.serving_size);

  const sizeParts = serving
    .split('/')
    .map((value) => cleanValue(value))
    .filter(Boolean);

  const priceParts = price
    .split('/')
    .map((value) => cleanValue(value))
    .filter(Boolean);

  // Perfect match: 375ml / 750ml + two prices
  if (sizeParts.length === priceParts.length && priceParts.length > 1) {
    return (
      <div className={styles.barPriceBlock}>
        {priceParts.map((priceValue, index) => (
          <span
            className={styles.barPriceUnit}
            key={`${sizeParts[index]}-${priceValue}`}
          >
            <span className={styles.barPriceLabel}>
              {sizeParts[index]}
            </span>
            {priceValue}
          </span>
        ))}
      </div>
    );
  }

  return <p className={styles.itemPrice}>{price}</p>;
};

// ─────────────────────────────────────────────────────────────────────────────
// SINGLE PRICE
// ─────────────────────────────────────────────────────────────────────────────

const SinglePrice = ({ item }) => {
  if (!hasValue(item.price)) return null;

  return <p className={styles.itemPrice}>{item.price}</p>;
};

// ─────────────────────────────────────────────────────────────────────────────
// ON TAP PRICE DISPLAY
// ─────────────────────────────────────────────────────────────────────────────

const TapPrice = ({ item }) => {
  const p250 = cleanValue(item.price_250ml);
  const p500 = cleanValue(item.price_500ml);

  if (!p250 && !p500) return null;

  return (
    <div className={styles.barPriceBlock}>
      {p250 && (
        <span className={styles.barPriceUnit}>
          <span className={styles.barPriceLabel}>250ml</span>
          {p250}
        </span>
      )}

      {p500 && (
        <span className={styles.barPriceUnit}>
          <span className={styles.barPriceLabel}>500ml</span>
          {p500}
        </span>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BAR PRICE DECISION
// ─────────────────────────────────────────────────────────────────────────────

const BarPrice = ({ item, subcategory }) => {
  const isWine =
    subcategory === 'Wines' ||
    subcategory === 'Rosé & Sparkling Wine';

  const hasSpiritPrices =
    hasValue(item.price_30ml) ||
    hasValue(item.price_60ml) ||
    hasValue(item.price_bottle);

  if (isWine) {
    return <WinePrice item={item} />;
  }

  if (hasSpiritPrices) {
    return <SpiritPrice item={item} />;
  }

  return <SinglePrice item={item} />;
};

// ─────────────────────────────────────────────────────────────────────────────
// MENU ITEM
// ─────────────────────────────────────────────────────────────────────────────

const MenuItem = ({
  item,
  hasVeg = false,
  category,
  subcategory = '',
}) => {
  const showSourceNote =
    category === 'On Tap' && hasValue(item.source_note);

  return (
    <article className={styles.menuItem}>
      <div className={styles.itemLeft}>
        <div className={styles.itemNameRow}>
          {hasVeg && <VegBadge isVeg={item.isVeg} />}

          <h4 className={styles.itemName}>{item.name}</h4>

          {isTruthy(item.isNew) && <NewBadge />}
        </div>

        {hasValue(item.description) && (
          <p className={styles.itemDesc}>{item.description}</p>
        )}

        {showSourceNote && (
          <p className={styles.itemDesc}>{item.source_note}</p>
        )}
      </div>

      <div className={styles.itemRight}>
        {category === 'On Tap' ? (
          <TapPrice item={item} />
        ) : category === 'Bar Menu' ? (
          <BarPrice item={item} subcategory={subcategory} />
        ) : (
          <VariantPrice item={item} />
        )}
      </div>
    </article>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORY ORDER
// ─────────────────────────────────────────────────────────────────────────────

const getSubcategoryOrder = (category) => {
  switch (category) {
    case 'Food Menu':
      return FOOD_SUBCATEGORIES;

    case 'Bar Menu':
      return BAR_SUBCATEGORIES;

    case 'Cocktails':
      return COCKTAIL_SUBCATEGORIES;

    case 'Mocktails':
      return MOCKTAIL_SUBCATEGORIES;

    case 'On Tap':
      return ON_TAP_SUBCATEGORIES;

    default:
      return [];
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// GROUP ITEMS
//
// 1. Group by subcategory.
// 2. Sort groups according to the PDF / Sheet.
// 3. Sort items using sort_order from Google Sheet.
// ─────────────────────────────────────────────────────────────────────────────

const groupMenuItems = (data, category) => {
  const groups = {};

  data.forEach((item) => {
    const subcategory = cleanValue(item.subcategory) || 'Other';

    if (!groups[subcategory]) {
      groups[subcategory] = [];
    }

    groups[subcategory].push(item);
  });

  Object.values(groups).forEach((items) => {
    items.sort(
      (a, b) =>
        parseNumber(a.sort_order, 999999) -
        parseNumber(b.sort_order, 999999)
    );
  });

  const configuredOrder = getSubcategoryOrder(category);

  const orderedNames = [
    ...configuredOrder.filter((name) => groups[name]),
    ...Object.keys(groups).filter(
      (name) => !configuredOrder.includes(name)
    ),
  ];

  return orderedNames.map((name) => ({
    name,
    items: groups[name],
  }));
};

// ─────────────────────────────────────────────────────────────────────────────
// SUBCATEGORY HEADER
// ─────────────────────────────────────────────────────────────────────────────

const SubcategoryHeader = ({ title }) => (
  <div className={styles.subcategoryHeader}>
    <h3 className={styles.subcategoryTitle}>{title}</h3>
    <div className={styles.subcategoryLine} />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// FOOD / GENERIC SUBCATEGORY
// ─────────────────────────────────────────────────────────────────────────────

const SubcategoryBlock = ({
  title,
  items,
  hasVeg,
  category,
}) => (
  <section className={styles.subcategoryBlock}>
    <SubcategoryHeader title={title} />

    <div className={styles.itemsList}>
      {items.map((item, index) => (
        <MenuItem
          key={`${item.name}-${index}`}
          item={item}
          hasVeg={hasVeg}
          category={category}
          subcategory={title}
        />
      ))}
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// BAR SUBCATEGORY
// Adds the appropriate price headers.
// ─────────────────────────────────────────────────────────────────────────────

const BarSubcategoryBlock = ({ title, items }) => {
  const isWine =
    title === 'Wines' ||
    title === 'Rosé & Sparkling Wine';

  const usesSpiritPricing = items.some(
    (item) =>
      hasValue(item.price_30ml) ||
      hasValue(item.price_60ml) ||
      hasValue(item.price_bottle)
  );

  const usesWinePricing =
    isWine && items.some((item) => hasValue(item.price));

  return (
    <section className={styles.subcategoryBlock}>
      <SubcategoryHeader title={title} />

      {usesSpiritPricing && (
        <div className={styles.barColumnHeaders}>
          <span className={styles.barColItem}>Item</span>

          <span className={styles.barColPrices}>
            <span>30ml</span>
            <span>60ml</span>
            <span>Bottle</span>
          </span>
        </div>
      )}

      {usesWinePricing && (
        <div className={styles.barColumnHeaders}>
          <span className={styles.barColItem}>Wine</span>

          <span className={styles.barColPrices}>
            <span>Glass / Size</span>
            <span>Price</span>
          </span>
        </div>
      )}

      <div className={styles.itemsList}>
        {items.map((item, index) => (
          <MenuItem
            key={`${item.name}-${index}`}
            item={item}
            hasVeg={false}
            category="Bar Menu"
            subcategory={title}
          />
        ))}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ON TAP HEADER
// ─────────────────────────────────────────────────────────────────────────────

const OnTapBlock = ({ title, items }) => (
  <section className={styles.subcategoryBlock}>
    <SubcategoryHeader title={title} />

    <div className={styles.barColumnHeaders}>
      <span className={styles.barColItem}>Beer</span>

      <span className={styles.barColPrices}>
        <span>250ml</span>
        <span>500ml</span>
      </span>
    </div>

    <div className={styles.itemsList}>
      {items.map((item, index) => (
        <MenuItem
          key={`${item.name}-${index}`}
          item={item}
          hasVeg={false}
          category="On Tap"
          subcategory={title}
        />
      ))}
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// COCKTAIL / MOCKTAIL GROUP
// ─────────────────────────────────────────────────────────────────────────────

const GenericGroupedMenu = ({ data, category }) => {
  const grouped = useMemo(
    () => groupMenuItems(data, category),
    [data, category]
  );

  return (
    <div className={styles.subcategoriesWrapper}>
      {grouped.map((group) => (
        <SubcategoryBlock
          key={group.name}
          title={group.name}
          items={group.items}
          hasVeg={false}
          category={category}
        />
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MENU CONTENT
// ─────────────────────────────────────────────────────────────────────────────

const MenuContent = ({
  category,
  data,
  status,
  error,
  onRetry,
}) => {
  if (status === 'loading') {
    return <LoadingState />;
  }

  if (status === 'error') {
    return (
      <ErrorState
        message={error || 'Unable to load this menu.'}
        onRetry={onRetry}
      />
    );
  }

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <p className={styles.emptyText}>
        Menu items coming soon.
      </p>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // FOOD
  // ───────────────────────────────────────────────────────────────────────────

  if (category === 'Food Menu') {
    const grouped = groupMenuItems(data, category);

    return (
      <div className={styles.subcategoriesWrapper}>
        {grouped.map((group) => (
          <SubcategoryBlock
            key={group.name}
            title={group.name}
            items={group.items}
            hasVeg={true}
            category="Food Menu"
          />
        ))}
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // BAR
  // ───────────────────────────────────────────────────────────────────────────

  if (category === 'Bar Menu') {
    const grouped = groupMenuItems(data, category);

    return (
      <div className={styles.subcategoriesWrapper}>
        {grouped.map((group) => (
          <BarSubcategoryBlock
            key={group.name}
            title={group.name}
            items={group.items}
          />
        ))}
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // ON TAP
  // ───────────────────────────────────────────────────────────────────────────

  if (category === 'On Tap') {
    const grouped = groupMenuItems(data, category);

    return (
      <div className={styles.subcategoriesWrapper}>
        {grouped.map((group) => (
          <OnTapBlock
            key={group.name}
            title={group.name}
            items={group.items}
          />
        ))}
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // COCKTAILS / MOCKTAILS
  // ───────────────────────────────────────────────────────────────────────────

  return (
    <GenericGroupedMenu
      data={data}
      category={category}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LOADING
// ─────────────────────────────────────────────────────────────────────────────

const LoadingState = () => (
  <div className={styles.loadingWrapper}>
    {[...Array(7)].map((_, index) => (
      <div
        key={index}
        className={styles.skeleton}
        style={{
          animationDelay: `${index * 60}ms`,
        }}
      >
        <div className={styles.skeletonName} />
        <div className={styles.skeletonDesc} />
        <div className={styles.skeletonPrice} />
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ERROR
// ─────────────────────────────────────────────────────────────────────────────

const ErrorState = ({ message, onRetry }) => (
  <div className={styles.errorWrapper}>
    <p className={styles.errorText}>{message}</p>

    <button
      type="button"
      className={styles.retryBtn}
      onClick={onRetry}
    >
      Try Again
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────

const MenuPage = () => {
  const [activeCategory, setActiveCategory] =
    useState('Food Menu');

  const contentRef = useRef(null);

  const {
    allData,
    statuses,
    errors,
    refetch,
  } = useAllMenuData();

  const handleCategoryChange = useCallback(
    (category) => {
      setActiveCategory(category);

      // Let React render the new category first.
      requestAnimationFrame(() => {
        contentRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    },
    []
  );

  const activeData = allData[activeCategory] || [];

  return (
    <>
      <HeroSection
        title="Our Menu"
        image="/images/23-DSC09427.jpg"
        imageAlt="Berli food spread"
      />

      <section className={styles.menuSection}>
        <div className={styles.menuContainer}>

          {/* ───────────────────────────────────────────────────────────────────
              MAIN CATEGORY SELECTOR
          ─────────────────────────────────────────────────────────────────── */}

          <div className={styles.selectorWrapper}>
            <nav
              className={styles.categoryNav}
              role="tablist"
              aria-label="Menu categories"
            >
              {MAIN_CATEGORIES.map((category) => {
                const isActive =
                  activeCategory === category;

                const status =
                  statuses[category];

                return (
                  <button
                    key={category}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`menu-panel-${category
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`}
                    className={`${styles.categoryBtn} ${
                      isActive
                        ? styles.categoryBtnActive
                        : ''
                    }`}
                    onClick={() =>
                      handleCategoryChange(category)
                    }
                  >
                    <span>{category}</span>

                    {status === 'loading' && (
                      <span
                        className={styles.tabDot}
                        aria-label="Loading"
                      />
                    )}

                    {status === 'error' && (
                      <span
                        className={styles.tabDotError}
                        aria-label="Error"
                      />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* ───────────────────────────────────────────────────────────────────
              CONTENT
          ─────────────────────────────────────────────────────────────────── */}

          <div
            ref={contentRef}
            id={`menu-panel-${activeCategory
              .toLowerCase()
              .replace(/\s+/g, '-')}`}
            className={styles.contentWrapper}
            role="tabpanel"
            aria-label={activeCategory}
          >
            <div className={styles.contentHeader}>
              <h2 className={styles.contentTitle}>
                {activeCategory}
              </h2>

              <div
                className={styles.contentDivider}
              />
            </div>

            <MenuContent
              category={activeCategory}
              data={activeData}
              status={
                statuses[activeCategory] || 'loading'
              }
              error={errors[activeCategory]}
              onRetry={refetch}
            />
          </div>

          {/* ───────────────────────────────────────────────────────────────────
              BOTTOM NOTE
          ─────────────────────────────────────────────────────────────────── */}

          <div className={styles.allergenNote}>
            <p>
              Please inform your server of any
              allergies or dietary requirements before
              ordering.
            </p>

            <p>
              All prices are inclusive of taxes.
            </p>

            <div className={styles.legendRow}>
              <span className={styles.legendItem}>
                <span
                  className={styles.vegDotInline}
                />
                Vegetarian
              </span>

              <span className={styles.legendItem}>
                <span
                  className={styles.nonVegDotInline}
                />
                Non-Vegetarian
              </span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MenuPage;